/**
 * Gobodo Retry Utility
 * 
 * Retry logic for transient Gobodo API failures with exponential backoff
 */

import { isRetryableError } from './gobodoErrorHandler';

export interface RetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  retryableStatusCodes?: number[];
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  retryableStatusCodes: [429, 500, 502, 503, 504],
};

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculate delay for retry attempt with exponential backoff + jitter.
 * Jitter randomises the delay between 50-150% of the base to prevent
 * concurrent retries from colliding at the same instant.
 */
function calculateDelay(attempt: number, options: Required<RetryOptions>): number {
  const base = options.initialDelayMs * Math.pow(options.backoffMultiplier, attempt);
  const jittered = base * (0.5 + Math.random());
  return Math.min(jittered, options.maxDelayMs);
}

/**
 * Retry a function with exponential backoff
 * 
 * @param fn - Function to retry (should return a Promise)
 * @param options - Retry configuration options
 * @returns Result of the function call
 * @throws Last error if all retries fail
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: any;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Don't retry if error is not retryable
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt >= opts.maxRetries) {
        break;
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempt, opts);
      await sleep(delay);
    }
  }

  // All retries exhausted
  throw lastError;
}

/**
 * Retry a function with exponential backoff, but only for specific status codes
 */
export async function retryForStatusCodes<T>(
  fn: () => Promise<T>,
  statusCodes: number[],
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: any;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      const status = error.response?.status;
      const shouldRetry = status && statusCodes.includes(status);

      if (!shouldRetry) {
        throw error;
      }

      if (attempt >= opts.maxRetries) {
        break;
      }

      const delay = calculateDelay(attempt, opts);
      await sleep(delay);
    }
  }

  throw lastError;
}
